import { useState, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import aiService from 'api/services/ai';
import { conversationsService } from 'api/services/conversations';
import { TIMING, DEFAULT_MESSAGES } from '../utils/constants';

/**
 * Create a welcome message object
 */
export const createWelcomeMessage = () => ({
  id: 1,
  type: 'assistant',
  content: DEFAULT_MESSAGES.WELCOME,
  timestamp: new Date(),
});

/**
 * useGigabotStreaming Hook
 *
 * Manages AI streaming, message state, and conversation persistence.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.sessionId - Session ID for the chat
 * @param {string} options.context - Page context for AI responses
 * @param {Object} options.contextData - Context-specific data
 * @param {string} options.selectedModel - Selected AI model
 * @param {boolean} options.webSearchEnabled - Whether web search is enabled
 * @param {string} options.environment - Conversation environment
 * @param {Function} options.onMessage - Callback when message is received
 * @param {Function} options.onUserInput - Callback when user sends input
 * @param {Function} options.addProposal - Function to add action proposals
 * @param {Object} options.initialMessage - Initial welcome message
 *
 * @returns {Object} Streaming state and handlers
 */
export const useGigabotStreaming = ({
  sessionId: externalSessionId = null,
  context = null,
  contextData = null,
  selectedModel = '',
  webSearchEnabled = false,
  environment = 'global',
  onMessage = null,
  onUserInput = null,
  addProposal = null,
  initialMessage = null,
  prefilledContent = '',
} = {}) => {
  // Session ID with localStorage persistence (scoped by environment)
  const [sessionId, setSessionId] = useState(() => {
    if (externalSessionId) return externalSessionId;
    const storageKey = `gigabot_session_id_${environment}`;
    const stored = localStorage.getItem(storageKey);
    if (stored) return stored;
    const newId = uuidv4();
    localStorage.setItem(storageKey, newId);
    return newId;
  });

  // State
  const [messages, setMessages] = useState(() => {
    if (initialMessage) {
      return [initialMessage];
    }
    return [createWelcomeMessage()];
  });
  const [input, setInput] = useState(prefilledContent);
  const [isLoading, setIsLoading] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);

  // Refs
  const inputRef = useRef(null);

  /**
   * Extract structured data from context
   */
  const extractStructuredData = useCallback((ctxData) => {
    if (!ctxData) return null;

    const structured = {};

    if (ctxData.milestones && Array.isArray(ctxData.milestones)) {
      structured.milestones = ctxData.milestones;
    }

    if (ctxData.requiredSkills && Array.isArray(ctxData.requiredSkills)) {
      structured.skills = ctxData.requiredSkills;
    } else if (ctxData.skills && Array.isArray(ctxData.skills)) {
      structured.skills = ctxData.skills;
    }

    if (ctxData.budget) {
      structured.budget = ctxData.budget;
    } else if (ctxData.amountUsd) {
      structured.budget = {
        value: ctxData.amountUsd.value || ctxData.amountUsd,
        confidence: ctxData.amountUsd.confidence || 1.0,
      };
    }

    return Object.keys(structured).length > 0 ? structured : null;
  }, []);

  /**
   * Handle streaming response from AI
   */
  const handleStreaming = useCallback(
    async (message, conversationId = null) => {
      const loadingTimeout = setTimeout(() => {
        console.warn(
          'Gigabot: Streaming timeout reached, resetting loading state'
        );
        setIsLoading(false);
      }, TIMING.STREAMING_TIMEOUT);

      try {
        const streamingMessage = {
          id: Date.now() + 1,
          type: 'assistant',
          content: '',
          timestamp: new Date(),
          isStreaming: true,
        };

        setMessages((prev) => [...prev, streamingMessage]);

        const callbacks = {
          onSessionUpdate: () => {},
          onChunk: (chunkData) => {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === streamingMessage.id
                  ? { ...msg, content: chunkData.content, isStreaming: true }
                  : msg
              )
            );
          },
          onStreamComplete: async (completeData) => {
            clearTimeout(loadingTimeout);

            const structuredData = extractStructuredData(
              completeData.context_data
            );

            let actionProposal = null;
            if (completeData.action_proposal) {
              actionProposal = {
                ...completeData.action_proposal,
                id: completeData.action_proposal.id || `proposal-${Date.now()}`,
              };
              if (addProposal) {
                addProposal(actionProposal);
              }
            }

            const finalMessage = {
              id: streamingMessage.id,
              type: 'assistant',
              content: completeData.content,
              isStreaming: false,
              timestamp: completeData.timestamp
                ? new Date(completeData.timestamp)
                : streamingMessage.timestamp,
              sdui: completeData.sdui,
              context_data: completeData.context_data,
              structuredData: structuredData,
              actionProposal: actionProposal,
              sessionId: completeData.sessionId,
              messageId: completeData.messageId,
              model: completeData.model,
            };

            setMessages((prev) =>
              prev.map((msg) => {
                if (msg.id === streamingMessage.id) {
                  return {
                    ...finalMessage,
                    actionProposal:
                      actionProposal || msg.actionProposal || null,
                  };
                }
                return msg;
              })
            );

            if (conversationId) {
              try {
                await conversationsService.createMessage(conversationId, {
                  role: 'assistant',
                  content: completeData.content,
                  modelUsed: completeData.model,
                  structuredData: completeData.sdui,
                  contextData: completeData.context_data,
                });
              } catch (error) {
                console.error('Failed to save assistant message:', error);
              }
            }

            if (onMessage) {
              onMessage(finalMessage);
            }

            setIsLoading(false);
          },
          onError: (error) => {
            console.error('Gigabot: Streaming error:', error);
            clearTimeout(loadingTimeout);

            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === streamingMessage.id
                  ? {
                      ...msg,
                      content:
                        'Sorry, I encountered an error. Please try again.',
                      isStreaming: false,
                    }
                  : msg
              )
            );
            setIsLoading(false);
          },
        };

        await aiService.chat(message, {
          sessionId: sessionId,
          chatContext: context || 'default',
          contextData: contextData,
          model: selectedModel || null,
          stream: true,
          webSearchEnabled: webSearchEnabled,
          callbacks,
        });
      } catch (error) {
        console.error('Gigabot: Streaming error in try-catch:', error);
        clearTimeout(loadingTimeout);

        const errorMessage = {
          id: Date.now() + 1,
          type: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date(),
          isStreaming: false,
        };

        setMessages((prev) => [...prev, errorMessage]);
        setIsLoading(false);
      }
    },
    [
      sessionId,
      context,
      contextData,
      selectedModel,
      webSearchEnabled,
      extractStructuredData,
      addProposal,
      onMessage,
    ]
  );

  /**
   * Move to next blank in template
   */
  const moveToNextBlank = useCallback(() => {
    const currentContent = input;
    const firstBlankIndex = currentContent.indexOf('_____');

    if (firstBlankIndex === -1) return false;

    const before = currentContent.substring(0, firstBlankIndex);
    const after = currentContent.substring(firstBlankIndex + 5);
    const newContent = before + after;

    setInput(newContent);

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(firstBlankIndex, firstBlankIndex);
      }
    }, 0);

    return true;
  }, [input]);

  /**
   * Send a message
   */
  const sendMessage = useCallback(async () => {
    const msg = input.trim();
    if (!msg || isLoading) return;

    if (input.includes('_____')) {
      moveToNextBlank();
      return;
    }

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: msg,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    if (onUserInput) {
      onUserInput(msg);
    }

    setTimeout(() => inputRef.current?.focus(), 0);

    let conversationId = activeConversationId;
    if (!conversationId) {
      try {
        const convResult = await conversationsService.createConversation({
          contextType: context || 'chat',
          environment: environment,
          title: msg.substring(0, 100),
        });
        if (convResult.success && convResult.data) {
          conversationId = convResult.data.conversation_id;
          setActiveConversationId(conversationId);
        }
      } catch (error) {
        console.error('Failed to create conversation:', error);
      }
    }

    if (conversationId) {
      try {
        await conversationsService.createMessage(conversationId, {
          role: 'user',
          content: msg,
        });
      } catch (error) {
        console.error('Failed to save user message:', error);
      }
    }

    await handleStreaming(msg, conversationId);
  }, [
    input,
    isLoading,
    activeConversationId,
    context,
    environment,
    onUserInput,
    moveToNextBlank,
    handleStreaming,
  ]);

  /**
   * Delete a message
   */
  const deleteMessage = useCallback((messageId) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
  }, []);

  /**
   * Reset messages to welcome state
   */
  const resetMessages = useCallback(() => {
    setMessages([createWelcomeMessage()]);
    setActiveConversationId(null);
  }, []);

  /**
   * Set messages directly (for loading conversations)
   */
  const setMessagesDirectly = useCallback((newMessages) => {
    setMessages(newMessages);
  }, []);

  return {
    // State
    messages,
    input,
    isLoading,
    activeConversationId,
    sessionId,
    inputRef,

    // Setters
    setInput,
    setSessionId,
    setActiveConversationId,
    setMessages: setMessagesDirectly,

    // Handlers
    sendMessage,
    deleteMessage,
    resetMessages,
    handleStreaming,
    moveToNextBlank,
  };
};

export default useGigabotStreaming;
